import React, { Dispatch, ReactElement, SetStateAction, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import Flex from 'ui/Flex/Flex';
import { DashboardChartT } from 'business/definitions';
import { Button, IconButton } from '@mui/material';
import SimpleModal from 'ui/Modals/SimpleModal';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ModalContent from './ModalContent';
import { AlertAction, AlertActionContext } from './Contexts/AlertActionContext';
import { ModalContext } from './Contexts/ModalContext';
import AlertActionProvider from 'components/ChartAlert/Contexts/AlertActionProvider';
import ModalContextProvider from 'components/ChartAlert/Contexts/ModalContextProvider';

type Props = {
    chart: DashboardChartT;
    chartTitle?: string | null;
    show: boolean;
    closeModal: () => void;
};

const StyDrawer = styled(Drawer, {
    name: 'StyDrawer',
})(({ theme }) => ({
    '> .MuiPaper-root': {
        width: '32vw',
        padding: theme.spacing(3),
    },
}));

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})({
    display: 'flex',
    alignItems: 'center',
});

const StyCloseIcon = styled(CloseIcon, {
    name: 'StyCloseIcon',
})({
    float: 'right',
    cursor: 'pointer',
});

const ChartAlertModal = ({
    chart,
    chartTitle,
    show,
    closeModal,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
        useState(false);
    const [closeFunc, setCloseFunc] = useState(() => () => {});

    const closeDialogAndModal = () => {
        setIsConfirmationDialogOpen(false);
        closeFunc();
    };

    // Execute a the function `func` if `isDirty` is False.
    // Otherwise, open the confirmation dialog and use `func` as its close function.
    const executeIfNotDirty = (isDirty: boolean, func: () => void) => () => {
        if (isDirty) {
            setCloseFunc(() => func);
            setIsConfirmationDialogOpen(true);
        } else {
            func();
        }
    };

    const getActionTitle = (
        isModalDirty: boolean,
        action: AlertAction,
        setAction: Dispatch<SetStateAction<AlertAction>>,
    ): ReactElement => {
        if (['create', 'edit', 'status'].includes(action.name)) {
            return (
                <StyTitle variant="h4">
                    <IconButton
                        onClick={executeIfNotDirty(isModalDirty, () =>
                            setAction({ name: 'list' }),
                        )}
                    >
                        <ChevronLeft />
                    </IconButton>
                    <div>{t(`alert.${action.name}.title`)}</div>
                </StyTitle>
            );
        }
        return (
            <StyTitle variant="h4">{t(`alert.${action.name}.title`)}</StyTitle>
        );
    };

    return (
        <ErrorBoundary>
            <ModalContextProvider>
                <ModalContext.Consumer>
                    {({ isDirty, setDirty }) => (
                        <AlertActionProvider>
                            <AlertActionContext.Consumer>
                                {({ action, setAction }) => (
                                    <StyDrawer
                                        onContextMenu={(e) => {
                                            e.stopPropagation();
                                        }}
                                        anchor="right"
                                        open={show}
                                        ModalProps={{
                                            onBackdropClick: executeIfNotDirty(
                                                isDirty,
                                                closeModal,
                                            ),
                                        }}
                                    >
                                        <SimpleModal
                                            open={isConfirmationDialogOpen}
                                            onClose={() =>
                                                setIsConfirmationDialogOpen(
                                                    false,
                                                )
                                            }
                                            isSmallHeight
                                            disableClosing
                                            additionalButtons={[
                                                <Button
                                                    key="cancel-button"
                                                    onClick={() =>
                                                        setIsConfirmationDialogOpen(
                                                            false,
                                                        )
                                                    }
                                                >
                                                    {t(
                                                        'alert.confirmationDialog.cancel',
                                                    )}
                                                </Button>,
                                                <Button
                                                    key="confirm-button"
                                                    variant="contained"
                                                    onClick={() => {
                                                        closeDialogAndModal();
                                                        setDirty(false);
                                                    }}
                                                    autoFocus
                                                >
                                                    {t(
                                                        'alert.confirmationDialog.confirm',
                                                    )}
                                                </Button>,
                                            ]}
                                        >
                                            {t('alert.confirmationDialog.body')}
                                        </SimpleModal>
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                        >
                                            {getActionTitle(
                                                isDirty,
                                                action,
                                                setAction,
                                            )}
                                            <StyCloseIcon
                                                onClick={executeIfNotDirty(
                                                    isDirty,
                                                    closeModal,
                                                )}
                                                fontSize="large"
                                            />
                                        </Flex>
                                        <ModalContent
                                            chart={chart}
                                            action={action}
                                            chartTitle={
                                                chartTitle || chart.title
                                            }
                                        />
                                    </StyDrawer>
                                )}
                            </AlertActionContext.Consumer>
                        </AlertActionProvider>
                    )}
                </ModalContext.Consumer>
            </ModalContextProvider>
        </ErrorBoundary>
    );
};

export default ChartAlertModal;
